// @ts-check

import cleanSource from '@/helpers/clean-source'
import moment from 'moment'
import { updateField } from 'vuex-map-fields'

import { entryCountry, entryItemType, supplierType } from '@/core/constants'
import {
  ASSIGNMENT_CREATE_EMPTY_POSTING_ROW,
  ASSIGNMENT_REMOVE_ITEM,
  ASSIGNMENT_REMOVE_ITEMS
} from '@/store/mutation-types'
import { createPaymentReduction, createPostingRow, ocrState } from './utils'

/**
 * Applies the given properties to supplier.
 * @param {EntryModuleState} state
 * @param {Partial<Supplier>} newProps
 */
export const updateSupplier = (state, newProps) => {
  state.supplier = { ...state.supplier, ...newProps }
}

/**
 * Sets the expense item resources.
 * @param {EntryModuleState} state
 * @param {EntryItemResource[]} items
 */
export const setItems = (state, items) => {
  state.items = items
}

/**
 * Removes item from the given index.
 * @param {EntryModuleState} state
 * @param {number} index
 */
export const removeItem = (state, index) => {
  state.items.splice(index, 1)
}

/**
 * Removes all items from the given indexes
 * @param {EntryModuleState} state
 * @param {number[]} indexes
 */
export const removeItems = (state, indexes) => {
  state.items = state.items.filter((item, index) => !indexes.includes(index))
}

/**
 * Sets the type of the entry.
 * @param {EntryModuleState} state
 * @param {EntryType} type
 */
export const setEntryType = (state, type) => {
  state.type = type
}

/**
 * Clears store data.
 * @param {EntryModuleState} state
 */
export const clearData = (state) => {
  state.id = null
  state.type = null
  state.date = moment(new Date()).format('YYYY-MM-DD')
  state.description = ''
  state.items = []

  state.files = []
  state.paymentReductions = []
  state.hasPaymentReductions = false

  state.ocrState = ocrState.IDLE
  state.ocrData = null

  state.supplier = {
    country: entryCountry.FINLAND,
    type: supplierType.COMPANY,
    name: null,
    businessId: null,
    existingId: null
  }

  state.entry = null
}

/**
 * @param {EntryModuleState} state
 * @param {Supplier['businessId']} businessId
 */
export const updateSupplierBusinessId = (state, businessId) =>
  updateSupplier(state, { businessId })

/**
 * @param {EntryModuleState} state
 * @param {Supplier['country']} country
 */
export const updateSupplierCountry = (state, country) =>
  updateSupplier(state, { country })

/**
 * @param {EntryModuleState} state
 * @param {Supplier['name']} name
 */
export const updateSupplierName = (state, name) =>
  updateSupplier(state, { name })

/**
 * @param {EntryModuleState} state
 * @param {Supplier['type']} type
 */
export const updateSupplierType = (state, type) =>
  updateSupplier(state, { type })

/**
 * Updates store's supplier based on the ocr data.
 * @param {EntryModuleState} state
 * @param {AttachmentOcrResource} payload
 */
export const updateSupplierFromOcr = (state, payload) => {
  // TODO: Remove supplier_* fallback after 2025-01-05 (backward compatibility)
  const businessId =
    payload.counterparty_business_id || payload.supplier_business_id
  const name = payload.counterparty_name || payload.supplier_name
  const region = payload.counterparty_region || payload.supplier_region

  if (businessId) {
    updateSupplierBusinessId(state, state.supplier.businessId || businessId)
  }

  if (name) {
    updateSupplierName(state, state.supplier.name || name)
  }

  if (region) {
    updateSupplierCountry(state, region)
  }

  if (name || businessId || region) {
    updateSupplierType(state, 'company')
    state.supplier.existingId = null
  }
}

/**
 * Updates entry data based on the assignment fetched from the API.
 * @param {EntryModuleState} state
 * @param {EntryResource} payload Assignment resource fetched from API.
 */
export const setEntryData = (state, payload) => {
  const {
    supplier_type,
    supplier_business_id: businessId,
    supplier_id: supplierId,
    items,
    supplier_name: supplierName,
    payment_reductions: paymentReductions
  } = payload

  state.entry = payload

  let supplierType = supplier_type

  if (
    ['finnish_company', 'eu_company', 'other_company'].includes(
      supplier_type || state.supplier.type
    )
  ) {
    supplierType = 'company'
  }

  state.id = payload.id
  state.type = payload.type
  state.date = moment(payload.date).format('YYYY-MM-DD')
  state.description = payload.description ?? ''

  state.supplier = {
    type: supplierType ?? state.supplier.type,
    businessId: businessId ?? state.supplier.businessId,
    country: items.length ? items[0].country : state.supplier.country,
    name: supplierName ?? state.supplier.name,
    existingId: supplierId || null
  }

  state.items = items

  state.paymentReductions = paymentReductions.map(
    ({ amount, type, vat, country }) => ({
      amount,
      type,
      vat,
      country
    })
  )

  if (state.paymentReductions.length) {
    state.hasPaymentReductions = true
  }

  if (items.length === 0) {
    const newItem = createPostingRow(state.type, null)
    state.items = [newItem]
  }
}

/**
 * Updates an attachment file at the given index.
 * @param {EntryModuleState} state
 * @param {{ index: number, attachment: any }} payload
 */
export const updateFile = ({ files }, { index, attachment }) =>
  files.splice(index, 1, attachment)

/**
 * Creates new empty posting row.
 * @param {EntryModuleState} state
 */
export const createEmptyPostingRow = (
  state,
  country = state.supplier.country
) => {
  const newItem = createPostingRow(state.type, country)

  const items = cleanSource(state.items)
  items.push(newItem)
  state.items = items
  return newItem
}

/**
 * Updates items based on the ocr data.
 * @param {EntryModuleState} state
 * @param {AttachmentOcrItemResource[]} items
 */
export const updateItemsFromOcr = (state, items) => {
  items.forEach((item) => {
    const row = createEmptyPostingRow(state)
    row.posting_type = item.row_type ?? null
    row.price = item.total ?? 0
    row.vat = item.vat_percent ?? 0
  })
}

/**
 * Creates and adds a new payment reduction.
 * @param {EntryModuleState} state
 */
export const addPaymentReduction = ({ paymentReductions }) =>
  paymentReductions.push(createPaymentReduction())

/**
 * Updates paymentReductions based on the ocr data.
 * @param {EntryModuleState} state
 * @param {AttachmentOcrReductionItemResource[]} reductions
 */
export const updatePaymentReductionsFromOcr = (state, reductions) => {
  const mappedReductions = reductions.map((reduction) => {
    const emptyReduction = createPaymentReduction()
    return {
      ...emptyReduction,
      amount: reduction.total ?? 0,
      type: reduction.type ?? null,
      country: reduction.country ?? null,
      vat: reduction.vat_percent ?? 0
    }
  })

  state.paymentReductions = mappedReductions
  state.hasPaymentReductions = Boolean(mappedReductions.length)
}

/**
 * Updates state based on the ocr data.
 * @param {EntryModuleState} state
 * @param {?AttachmentOcrResource} ocrData
 */
export const setOcrData = (state, ocrData) => {
  if (!ocrData) {
    state.ocrData = ocrData
    return
  }

  updateSupplierFromOcr(state, ocrData)

  if (
    ocrData.data_group_by_vat?.length &&
    !(state.items || []).some(
      ({ posting_type, price }) => posting_type || price
    )
  ) {
    setItems(state, [])
    updateItemsFromOcr(state, ocrData.data_group_by_vat)
  }

  if (
    ocrData?.reductions.length &&
    !state.paymentReductions.length &&
    // Payment reductions are not visible in the UI if there are no sales items
    (state.items || []).some(
      ({ posting_type }) => posting_type === entryItemType.SALES
    )
  ) {
    updatePaymentReductionsFromOcr(state, ocrData.reductions)
  }

  state.ocrData = ocrData
}

/** @type {EntryMutationTree} */
export const assignmentMutations = {
  updateField,
  removePaymentReductions: (state) => {
    state.paymentReductions = []
    state.hasPaymentReductions = false
  },
  setOcrState: (state, newOcrState) => {
    state.ocrState = newOcrState
  },
  setOcrData,
  [ASSIGNMENT_CREATE_EMPTY_POSTING_ROW]: createEmptyPostingRow,
  [ASSIGNMENT_REMOVE_ITEM]: removeItem,
  [ASSIGNMENT_REMOVE_ITEMS]: removeItems,
  setItems,
  setEntryType,
  removePaymentReduction: (state, index) => {
    state.paymentReductions.splice(index, 1)
    state.hasPaymentReductions = Boolean(state.paymentReductions.length)
  },
  createEmptyPostingRow,
  setEntryData,
  setEntryId: (state, id) => {
    state.id = id
  },
  addPaymentReduction,
  clearData,
  updateFile,
  updateSupplier,
  setFiles: (state, attachments) => {
    state.files = attachments
  }
}

export default assignmentMutations
